
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './Event.css';

const ACTION_BADGE = {
  auction:         { label: "Auction",           color: "#ff6800" },
  raffle:          { label: "Raffle",            color: "#6b8cff" },
  challenge:       { label: "Challenge",         color: "#ffc107" },
  contest:         { label: "Contest",           color: "#ce93d8" },
  ama:             { label: "AMA",               color: "#42a5f5" },
  ugc_campaign:    { label: "UGC Campaign",      color: "#4caf50" },
  themed_day:      { label: "Themed Day",        color: "#ff7043" },
  collab_takeover: { label: "Collab / Takeover", color: "#26c6da" },
  poll_quiz:       { label: "Poll / Quiz",       color: "#ab47bc" },
};

const Event = () => {
  const { t } = useTranslation();
  const { tenant, eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [items, setItems] = useState([]);
  const [itemActions, setItemActions] = useState({}); // { itemId: [{ action, ... }] }
  const [itemImages, setItemImages] = useState({}); // { itemId: url } — first photo per item

  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/events/${tenant}/events/${eventId}`);
        setEvent(response.data.event);
      } catch (error) {
        console.error('Error fetching event:', error);
      }
    };

    const fetchEventItems = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/events/${tenant}/events/${eventId}/items`);
        const list = Array.isArray(response.data.items) ? response.data.items : [];
        setItems(list);
        // The items list carries no media; load each item's first photo.
        const pairs = await Promise.all(list.map(async (it) => {
          try {
            const r = await axios.get(`${baseUrl}/api/events/${tenant}/events/${eventId}/items/${it.id}/images`);
            const first = (r.data && r.data.images && r.data.images[0]) || null;
            return [it.id, first ? first.path : null];
          } catch {
            return [it.id, null];
          }
        }));
        setItemImages(Object.fromEntries(pairs.filter(([, url]) => url)));
      } catch (error) {
        console.error('Error fetching event items:', error);
      }
    };

    const fetchItemActions = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/events/${tenant}/events/${eventId}/item-actions`);
        if (response.data.success) {
          const grouped = {};
          (response.data.actions || []).forEach(a => {
            if (!grouped[a.itemid]) grouped[a.itemid] = [];
            grouped[a.itemid].push(a);
          });
          setItemActions(grouped);
        }
      } catch (error) {
        console.error('Error fetching item actions:', error);
      }
    };

    fetchEvent();
    fetchEventItems();
    fetchItemActions();
  }, [tenant, eventId, baseUrl]);

  const handleItemClick = (item) => {
    navigate(`/${tenant}/${eventId}/${item.id}/eventItemDetail`, { state: { item } });
  };

  const handleActionClick = (e, item, action) => {
    e.stopPropagation();
    navigate(`/${tenant}/${eventId}/${item.id}/actionDetail?type=${action.action}`);
  };

  if (!event) {
    return <p>{t('events.loadingEventText')}</p>;
  }

  return (
    <div className="event">
      <h1>{event.name}</h1>
      <p>{event.description}</p>
      <p>{t('events.typeLabel')}{event.type}</p>
      <p>{t('events.startDateLabel')}{new Date(event.startdate).toLocaleDateString()}</p>
      <p>{t('events.endDateLabel')}{new Date(event.enddate).toLocaleDateString()}</p>
      <div className="event-items">
        {items.map((item) => {
          const actions = itemActions[item.id] || [];
          return (
            <div
              key={item.id}
              className="event-item-card"
              onClick={() => handleItemClick(item)}
            >
              {itemImages[item.id] && <img src={itemImages[item.id]} alt={item.name} />}
              <h4>{item.name}</h4>
              <p>{item.description}</p>
              {actions.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                  {actions.map(a => {
                    const badge = ACTION_BADGE[a.action] || { label: a.action, color: "#8899b0" };
                    return (
                      <span
                        key={a.id}
                        onClick={(e) => handleActionClick(e, item, a)}
                        title={`View ${badge.label} details`}
                        style={{
                          display: "inline-block",
                          padding: "3px 10px",
                          borderRadius: 12,
                          fontSize: 11,
                          fontWeight: 700,
                          background: `${badge.color}20`,
                          color: badge.color,
                          cursor: "pointer",
                          border: `1px solid ${badge.color}40`,
                          transition: "all 0.15s",
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = `${badge.color}40`; }}
                        onMouseLeave={e => { e.currentTarget.style.background = `${badge.color}20`; }}
                      >
                        {badge.label}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Event;
